import { API_PREFIX } from 'shared/api/constants/api-constants'
import type { InvitationStatus } from 'modules/community/types/invitation-interface'

export const communityKeys = {
  /** `/api` */
  all: () => [API_PREFIX] as const,
  /** `/api/community` */
  communityGeneral: () => [...communityKeys.all(), '/community'] as const,

  /** `/api/community/community/${path}/autocomplete?searchBy=${query}` */
  searchSuggestions: (path?: string, topicPath?: string, query?: string) =>
    [
      ...communityKeys.communityGeneral(),
      '/autocomplete',
      { path, topicPath, searchBy: query },
    ] as const,

  /** `/api/comment` */
  commentGeneral: () => [...communityKeys.all(), '/comment'] as const,

  /** `/api/comment/post_infos` */
  postsCommentsGeneral: () => [...communityKeys.commentGeneral(), '/post_infos'] as const,
  postsCommentsInfo: (postIds: number[]) =>
    [...communityKeys.postsCommentsGeneral(), { postIds }] as const,

  /** `/api/community/secure/global/community/${path}/invitations/...` */
  invitations: {
    all: ['community', 'invitations'] as const,
    lists: () => [...communityKeys.invitations.all, 'list'] as const,
    list: (communityPath: string, status: InvitationStatus, page: number, searchBy: string) =>
      [...communityKeys.invitations.lists(), { communityPath, status, page, searchBy }] as const,
    resend: (communityPath: string) =>
      [...communityKeys.invitations.all, 'resend', { communityPath }] as const,
    invite: (communityPath: string) =>
      [...communityKeys.invitations.all, 'invite', { communityPath }] as const,
    cancel: (communityPath: string) =>
      [...communityKeys.invitations.all, 'cancel', { communityPath }] as const,
    validity: (communityPath: string, invitationToken: string, confirmationToken: string) =>
      [
        ...communityKeys.invitations.all,
        'validity',
        { communityPath, invitationToken, confirmationToken },
      ] as const,
    accept: (communityPath: string) =>
      [...communityKeys.invitations.all, 'accept', { communityPath }] as const,
  },

  /** `/api/community/community/${path}/members/...` */
  members: {
    all: ['community', 'members'] as const,
    lists: () => [...communityKeys.members.all, 'list'] as const,
    list: (communityPath: string, page: number, searchBy: string) =>
      [...communityKeys.members.lists(), { communityPath, page, searchBy }] as const,
  },

  /** `/api/community/community/${path}/banned-users/...` */
  bannedMembers: {
    all: ['community', 'banned-members'] as const,
    lists: () => [...communityKeys.bannedMembers.all, 'list'] as const,
    list: (communityPath: string, page: number, searchBy: string) =>
      [...communityKeys.bannedMembers.lists(), { communityPath, page, searchBy }] as const,
  },
} as const

export const communityPostKeys = {
  all: ['community', 'posts'] as const,

  /** `/api/community/community/${communityPath}/posts/list` */
  lists: () => [...communityPostKeys.all, 'list'] as const,
  list: (communityPath: string) => [...communityPostKeys.lists(), { communityPath }] as const,

  /** `/api/community/community/${communityPath}/topic/${topicPath}/posts/list` */
  topicLists: () => [...communityPostKeys.all, 'topic-list'] as const,
  topicList: (communityPath: string, topicPath: string) =>
    [...communityPostKeys.topicLists(), { communityPath, topicPath }] as const,

  /** `/api/community/community/${communityPath}/posts/pinned` */
  /** `/api/community/community/${communityPath}/topic/${topicPath}/posts/pinned` */
  pinnedLists: () => [...communityPostKeys.all, 'pinned'] as const,
  pinnedList: (communityPath?: string, topicPath?: string) =>
    [...communityPostKeys.pinnedLists(), { communityPath, topicPath }] as const,

  /** `/api/community/community/${communityPath}/moderation/posts/scheduled/list` */
  scheduledLists: () => [...communityPostKeys.all, 'scheduled'] as const,
  scheduledList: (communityPath: string) =>
    [...communityPostKeys.scheduledLists(), { communityPath }] as const,

  /** `/api/community/community/${communityPath}/my-pending-posts/list` */
  pendingLists: () => [...communityPostKeys.all, 'pending'] as const,
  pendingList: (communityPath: string) =>
    [...communityPostKeys.pendingLists(), { communityPath }] as const,

  /** `/api/community/community/${communityPath}/topic/${topicPath}/post/${postPath}` */
  details: () => [...communityPostKeys.all, 'detail'] as const,
  detail: (communityPath: string, topicPath: string, postPath: string) =>
    [...communityPostKeys.details(), { communityPath, topicPath, postPath }] as const,
} as const

export const communityReportKeys = {
  all: ['community', 'report'] as const,

  /** `/api/.../report/moderation/targets/{type}/list` and `/history/{type}/list` */
  targets: {
    all: ['community', 'report', 'targets'] as const,

    /** `.../targets/{type}/list` */
    lists: () => [...communityReportKeys.targets.all, 'list'] as const,
    list: (communityPath: string) =>
      [...communityReportKeys.targets.lists(), { communityPath }] as const,

    /** `.../targets/{targetId}/reports/list` */
    reportsLists: () => [...communityReportKeys.targets.all, 'reports-list'] as const,
    reportsList: (communityPath: string, targetId: number) =>
      [...communityReportKeys.targets.reportsLists(), { communityPath, targetId }] as const,

    /** `.../history/{type}/list` */
    historyLists: () => [...communityReportKeys.targets.all, 'history-list'] as const,
    historyList: (communityPath: string) =>
      [...communityReportKeys.targets.historyLists(), { communityPath }] as const,

    /** `.../targets/count` */
    counts: () => [...communityReportKeys.targets.all, 'count'] as const,
    count: (communityPath: string) =>
      [...communityReportKeys.targets.counts(), { communityPath }] as const,

    target: (communityPath: string, targetId: number) =>
      [...communityReportKeys.targets.all, 'target', { communityPath, targetId }] as const,
  },

  /** `.../report/moderation/report/{reportId}` */
  details: () => [...communityReportKeys.all, 'detail'] as const,
  detail: (communityPath: string, reportId: number) =>
    [...communityReportKeys.details(), { communityPath, reportId }] as const,

  /** `.../report/moderation/filter/moderators` */
  moderators: {
    all: ['community', 'report', 'moderators'] as const,
    list: (communityPath: string) =>
      [...communityReportKeys.moderators.all, 'list', { communityPath }] as const,
  },
} as const
